<template>
  <a-table
    class="table datatable thead-light"
    :row-key="(record) => record.id || `${record.date}-${record.employee_id}`"
    :columns="columns"
    :data-source="dataSource"
    :loading="store.loading"
    :row-selection="rowSelection"
    :pagination="false"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Employee'">
        <div class="d-flex align-items-center file-name-icon">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="avatarSrc(record)"
              :name="record.employee_name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <h6 class="fw-medium">
              <a href="javascript:void(0);">{{ record.employee_name }}</a>
            </h6>
            <span class="fs-12 fw-normal">{{ record.department || "-" }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'Status'">
        <div class="d-flex flex-column align-items-start gap-1">
          <span
            class="d-inline-flex align-items-center"
            :class="[
              'badge',
              record.status_label === 'Present'
                ? 'badge-success-transparent'
                : record.status_label === 'Late'
                ? 'badge-warning-transparent'
                : record.status_label === 'Permission'
                ? 'badge-info-transparent'
                : 'badge-danger-transparent',
            ]"
          >
            <i class="ti ti-point-filled me-1"></i>{{ record.status_label || record.status || "-" }}
          </span>
          <span
            v-if="record.auto_closed"
            class="badge badge-soft-warning badge-xs"
            title="System closed this punch at shift end"
          >
            <i class="ti ti-clock-pause me-1"></i>Auto-closed
          </span>
        </div>
      </template>

      <template v-else-if="column.key === 'check_in'">
        {{ timeHHMM(record.check_in_at) }}
      </template>

      <template v-else-if="column.key === 'check_out'">
        <div class="d-flex flex-column align-items-start gap-1">
          <span>{{ timeHHMM(record.check_out_at) }}</span>
          <span
            v-if="record.auto_closed"
            class="badge badge-soft-warning badge-xs"
            title="System closed this punch at shift end"
          >
            <i class="ti ti-clock-pause me-1"></i>Auto-closed
          </span>
        </div>
      </template>

      <template v-else-if="column.key === 'break'">
        {{ minutesToLabel(record.break_minutes) }}
      </template>

      <template v-else-if="column.key === 'late'">
        <span
          v-if="(record.late_minutes || 0) > 0"
          class="d-inline-flex align-items-center badge badge-warning-transparent"
        >
          <i class="ti ti-clock-exclamation me-1"></i>{{ minutesToLabel(record.late_minutes) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template v-else-if="column.key === 'production'">
        <span
          class="d-inline-flex align-items-center"
          :class="[
            'badge',
            (record.total_work_minutes || 0) >= 9 * 60
              ? 'badge-info'
              : (record.total_work_minutes || 0) < 60
              ? 'badge-danger'
              : 'badge-success',
          ]"
        >
          <i class="ti ti-clock-hour-11 me-1"></i>{{ minutesToLabel(record.total_work_minutes) }}
        </span>
      </template>

      <template v-else-if="column.key === 'action'">
        <div v-if="isEditable(record)" class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            v-can="'Attendance.Edit'"
            title="Edit attendance"
            @click="$emit('edit', record)"
          >
            <i class="ti ti-edit"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            v-can="'Attendance.Delete'"
            title="Delete attendance"
            @click="$emit('delete', record)"
          >
            <i class="ti ti-trash"></i>
          </button>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";
import { useAttendanceEmployeeStore } from "@/stores/attendanceEmployeeStore";
import DqAvatar from "@/components/shared/DqAvatar.vue";
import { formatAttendanceTime } from "@/utils/attendanceDateTime";

const store = useAttendanceEmployeeStore();

defineEmits(["edit", "delete"]);

function isEditable(record) {
  const id = Number(record?.id);
  return Number.isFinite(id) && id > 0;
}

const columns = [
  { sorter: false },
  {
    title: "Employee",
    dataIndex: "employee_name",
    key: "Employee",
    sorter: {
      compare: (a, b) =>
        (a.employee_name || "").localeCompare(b.employee_name || "", undefined, {
          sensitivity: "base",
        }),
    },
  },
  {
    title: "Status",
    dataIndex: "status_label",
    key: "Status",
    sorter: {
      compare: (a, b) =>
        (a.status_label || "").localeCompare(b.status_label || "", undefined, {
          sensitivity: "base",
        }),
    },
  },
  {
    title: "Check In",
    dataIndex: "check_in_at",
    key: "check_in",
    sorter: {
      compare: (a, b) => toTs(a.check_in_at) - toTs(b.check_in_at),
    },
  },
  {
    title: "Check Out",
    dataIndex: "check_out_at",
    key: "check_out",
    sorter: {
      compare: (a, b) => toTs(a.check_out_at) - toTs(b.check_out_at),
    },
  },
  {
    title: "Break",
    dataIndex: "break_minutes",
    key: "break",
    sorter: {
      compare: (a, b) => (a.break_minutes || 0) - (b.break_minutes || 0),
    },
  },
  {
    title: "Late",
    dataIndex: "late_minutes",
    key: "late",
    sorter: {
      compare: (a, b) => (a.late_minutes || 0) - (b.late_minutes || 0),
    },
  },
  {
    title: "Production Hours",
    dataIndex: "total_work_minutes",
    key: "production",
    sorter: {
      compare: (a, b) =>
        (a.total_work_minutes || 0) - (b.total_work_minutes || 0),
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
  },
];

const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

function toTs(v) {
  if (!v) return 0;
  const match = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (match) {
    const [, y, mo, d, h, mi] = match;
    return new Date(+y, +mo - 1, +d, +h, +mi).getTime();
  }
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function timeHHMM(v) {
  return formatAttendanceTime(v);
}

function minutesToLabel(m) {
  const minutes = Math.max(0, parseInt(m || 0, 10));
  const h = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
}

const dataSource = computed(() =>
  (store.records || []).map((r) => {
    const name =
      (r?.employee?.name || "").trim() ||
      (r?.employee?.id ? `Employee #${r.employee.id}` : "-");

    return {
      id: r.id,
      employee_id: r?.employee?.id,
      employee_name: name,
      department: r?.employee?.department || "-",
      status: r.status || "-",
      status_label: r.status_label || r.status || "-",
      check_in_at: r.check_in_at,
      check_out_at: r.check_out_at,
      break_minutes: r.break_minutes ?? 0,
      late_minutes: r.late_minutes ?? 0,
      total_work_minutes: r.total_work_minutes ?? 0,
      auto_closed: !!r.auto_closed,
      date: r.date,
      notes: r.notes,
      overtime_minutes: r.overtime_minutes ?? 0,
      employee: r.employee,
      raw: r,
    };
  })
);

function avatarSrc(record) {
  return record?.employee?.photo_url || record?.employee?.avatar_url || "";
}
</script>
